import { AsyncPipe, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AssetDto,
  DisplayGuestDto,
  DisplayUser,
  GalleryColorLabel,
  GalleryDto,
  GalleryFeedbackOptions,
  Section,
  TenantDto,
} from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  debounceTime,
  filter,
  firstValueFrom,
  lastValueFrom,
  map,
  mergeWith,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { ProfilePreviewComponent } from '../../../gallery/components/profile-preview/profile-preview.component';
import { GallerySettingsMenuComponent } from '../../../gallery/menus/gallery-settings-menu/gallery-settings-menu.component';
import { AssetStatusIconPipe } from '../../../gallery/pipes/asset-status-icon.pipe';
import { AssetStatusNamePipe } from '../../../gallery/pipes/asset-status-name.pipe';
import { AssetStatusOrderPipe } from '../../../gallery/pipes/asset-status-order.pipe';
import { GROWTHBOOK } from '../../../growthbook';
import { calcAssetSizeGroup } from '../../../lib/data-units';
import { debounce } from '../../../lib/debounce';
import { exifKeys } from '../../../lib/extract-exif';
import { sleep } from '../../../lib/sleep';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { ActivityService } from '../../../services/activity.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { ExifService } from '../../../services/exif.service';
import { GalleryMemberService } from '../../../services/gallery-member.service';
import { GalleryService } from '../../../services/gallery.service';
import { OrganizationService } from '../../../services/organization.service';
import { StateService } from '../../../services/state.service';
import { TagService } from '../../../services/tag.service';
import { UnseenActivityService } from '../../../services/unseen-activity.service';
import { UpdateService } from '../../../services/update.service';
import { DisplayGuestEmailDto, DisplayUserEmailDto, VisitorService } from '../../../services/visitor.service';
import { TogglerComponent } from '../../../shared-extra/components/toggler/toggler.component';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SmoothCounterComponent } from '../../../shared/components/smooth-counter/smooth-counter.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TranslateLabelPipe } from '../../../shared/pipes/translate-label.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { AssetUserFeedbackMap } from '../../interfaces/asset-user-feedback-map';
import { Filter } from '../../interfaces/filter.interface';
import { filterAssets, singleFilter } from '../../lib/filter-assets';

@Component({
  selector: 'app-advanced-mode-sidebar',
  templateUrl: './advanced-mode-sidebar.component.html',
  styleUrls: ['./advanced-mode-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TogglerComponent,
    FormsModule,
    SvgComponent,
    ButtonDirective,
    TranslatePipe,
    TranslateLabelPipe,
    AsyncPipe,
    ContextMenuComponent,
    ProfilePreviewComponent,
    SmoothCounterComponent,
    AvatarComponent,
    AssetStatusOrderPipe,
    AssetStatusNamePipe,
    AssetStatusIconPipe,
    SwitchComponent,
  ],
})
export class AdvancedModeSidebarComponent implements OnChanges, OnDestroy {
  private galleryService = inject(GalleryService);
  private dialog = inject(DialogService);
  private exifService = inject(ExifService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private organizationService = inject(OrganizationService);
  private galleryMemberService = inject(GalleryMemberService);
  private state = inject(StateService);
  private updateService = inject(UpdateService);
  private visitorService = inject(VisitorService);
  private tagService = inject(TagService);
  private assetService = inject(AssetService);
  private unseenActivityService = inject(UnseenActivityService);
  private activityService = inject(ActivityService);
  private platformId = inject<string>(PLATFORM_ID);
  @Input() gallery: GalleryDto;
  @Input() sections: Section[];
  @Input() assets: AssetDto[];
  @Input() _assets: AssetDto[];
  @Input() searchTerm: string;
  @Input() filters: Filter[];
  galleryPermissions = input<GalleryDto['permissions']>();
  @Input() allowedFeedbackOptions: GalleryFeedbackOptions[] = [];
  @Input() allowedAndEnabledFeedbackOptions: GalleryFeedbackOptions[] = [];
  @Input() colorLabels: GalleryColorLabel[] = [];
  @Input() isDesktop: boolean;
  @Output() onFilterChange = new EventEmitter();
  @Output() onSelectAll = new EventEmitter();
  @Input() people: {
    userOrGuest: DisplayUserEmailDto | DisplayGuestEmailDto;
    assetIds: string[];
    type: 'user' | 'guest';
  }[] = [];

  @ViewChild('peopleToggler') peopleToggler: TogglerComponent;
  @ViewChild('favouriteToggler') favouriteToggler: TogglerComponent;
  @ViewChild('statusToggler') statusToggler: TogglerComponent;
  @ViewChild('commentToggler') commentToggler: TogglerComponent;
  @ViewChild('annotationToggler') annotationToggler: TogglerComponent;
  @ViewChild('colorLabelToggler') colorLabelToggler: TogglerComponent;
  @ViewChild('exif1Toggler') exif1Toggler: TogglerComponent;
  @ViewChild('exif2Toggler') exif2Toggler: TogglerComponent;
  @ViewChild('tagsToggler') tagsToggler: TogglerComponent;
  @ViewChild('sizeToggler') sizeToggler: TogglerComponent;

  subGalleries: GalleryDto[] = [];
  dynamicFilterValues: { [key: string]: string[] } = {};
  assetCounts: Map<string, number> = new Map();
  emailMap: { [key: string]: string } = {};
  isTeamMember: boolean;
  personFilterActive = false;

  allFilters = {
    likes: { with: false, without: false },
    status: { no_status: false, approved: false, rejected: false, in_progress: false, needs_review: false },
    color_label: { null: false, red: false, orange: false, blue: false, purple: false, green: false },
    comments: { with: false, without: false },
    annotations: { with: false, without: false },
    unseen_activity: { with: false },
    people: {},
    iso: {},
    camera: {},
    lens: {},
    focalLength: {},
    aperture: {},
    shutterSpeed: {},
    tags: {},
    size: {},
  };

  tenantTags$ = this.tagService.tags$;

  dynamicFiltersCreated = new Subject<void>();
  destroyed$ = new Subject<void>();

  galleryAssetUserFeedbackMap$: Observable<AssetUserFeedbackMap>;

  private onDynamicFiltersCreated = this.dynamicFiltersCreated
    .pipe(debounceTime(100), takeUntil(this.destroyed$))
    .subscribe(() => {
      this.exif1Toggler?.calcBodyHeight();
      this.exif2Toggler?.calcBodyHeight();
      this.tagsToggler?.calcBodyHeight();
    });

  galleryTags$ = of(undefined).pipe(
    mergeWith(this.dynamicFiltersCreated),
    switchMap(() => {
      if (!this.galleryPermissions().can_edit) return of([]);
      return this.tagService.tags$.pipe(
        catchError(() => {
          console.error('Error fetching tags');
          return of([]);
        })
      );
    }),
    map(tags =>
      tags.filter(tag => {
        if (this.allFilters.tags[tag.id]) return true;
        return this._assets?.some(asset => asset.tags?.some(t => t.id === tag.id));
      })
    )
  );

  filtersChanged$ = new BehaviorSubject<void>(undefined);

  public growthBook = inject(GROWTHBOOK);
  showUnseenActivityFilter = false;

  constructor() {
    exifKeys.forEach(key => (this.dynamicFilterValues[key] = []));

    this.assetService.updates
      .pipe(
        takeUntil(this.destroyed$),
        filter(event => event && !event.isOptimistic)
      )
      .subscribe(() => {
        this.calcAssetCountsDebounced();
        this.calcShowUnseenActivityFilter();
      });

    this.unseenActivityService.unseenActivityChange$.pipe(takeUntil(this.destroyed$)).subscribe(change => {
      if (!change) return;
      this.calcAssetCountsDebounced();
      this.onFilterChange.emit(undefined);
      this.calcShowUnseenActivityFilter();
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (isPlatformServer(this.platformId)) return;
    if (changes.gallery) {
      this.calcShowUnseenActivityFilter();
      const tenantId = (this.gallery.tenant as TenantDto)?.id || (this.gallery.tenant as string);
      this.isTeamMember = !!this.state.tenants?.find(t => t.id === tenantId);
      this.filters.forEach(filter => {
        const g = this.allFilters[filter.key];
        if (!g) return;
        filter.values.forEach(value => (g[value] = true));
      });

      await lastValueFrom(this.createDynamicFilters());
    }

    this.getVisitors();

    if (changes.gallery) {
      await sleep(0);
      this.calcAssetCountsDebounced();
      this.personFilterActive = this.filters?.some(f => f.key === 'people');
    }
  }

  private async calcShowUnseenActivityFilter() {
    this.showUnseenActivityFilter = this._assets?.some(asset => this.state.unseenActivityCountMap?.get(asset.id) > 0);
    if (!this.showUnseenActivityFilter && this.allFilters.unseen_activity.with) {
      this.allFilters.unseen_activity.with = false;
      this.handleFilter('unseen_activity');
    }
  }

  private async getVisitors() {
    const tenantId = (this.gallery.tenant as TenantDto)?.id || (this.gallery.tenant as string);
    const tenant = this.state.tenants?.find(t => t.id === tenantId);
    if (!tenant) return;
    const [visitors, galleryMembers, teamMembers] = await Promise.all([
      firstValueFrom(this.visitorService.getGalleryVisitors(this.gallery.id)),
      firstValueFrom(this.galleryMemberService.getAllGalleryMembers(true)),
      firstValueFrom(this.organizationService.getMembers()),
    ]);
    this.emailMap = {};
    visitors.forEach(r => {
      const id = <string>(r.user?.['id'] ?? r.guest?.['id']);
      const userOrGuest = <DisplayUser>r.user || <DisplayGuestDto>r.guest;
      if (!userOrGuest) {
        console.error('Could not find user or guest');
        return;
      }
      this.emailMap[id] = userOrGuest['email'];
    });
    galleryMembers.forEach(gm => {
      this.emailMap[gm.user['id']] = gm.user['email'];
    });
    teamMembers.forEach(tm => {
      this.emailMap[tm.id] = tm.email;
    });
  }

  public createDynamicFilters() {
    this.dynamicFilterValues = {};
    const sets: { [key: string]: Set<string> } = {};
    exifKeys.forEach(key => (sets[key] = new Set()));

    this._assets.forEach(asset => {
      const exif = this.exifService.getExif(asset);
      exifKeys.forEach(key => sets[key].add(exif[key] || null));
    });

    let filtersUpdated = false;

    if (this.growthBook.isOn('size-filter')) {
      const uniqueSizes = this._assets
        .map(asset => asset.version_data.size)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort((a, b) => b - a)
        .map(v => calcAssetSizeGroup(BigInt(v)));
      this.dynamicFilterValues.size = Array.from(new Set(uniqueSizes));
    }

    exifKeys.forEach(key => {
      this.dynamicFilterValues[key] = Array.from(sets[key]);
      if (!this.allFilters[key]) {
        this.allFilters[key] = {};
      } else {
        for (const i in this.allFilters[key]) {
          if (!this.dynamicFilterValues[key].includes(i) && i !== 'null') {
            filtersUpdated = true;
            delete this.allFilters[key][i];

            for (let j = 0; j < this.filters.length; j++) {
              const filter = this.filters[j];
              if (filter.key !== key) continue;
              filter.values = filter.values.filter(v => v !== i);
              if (!filter.values.length) {
                this.filters.splice(j, 1);
                j--;
              }
            }
          }
        }
      }
      this.dynamicFilterValues[key].forEach(value => {
        this.allFilters[key][value] = this.allFilters[key][value] || false;
      });

      this.dynamicFilterValues[key].sort((a, b) => {
        if (!a) return 1;
        if (!b) return -1;
        const intA = parseFloat(a);
        const intB = parseFloat(b);
        if (isNaN(intA) || isNaN(intB)) return a > b ? 1 : -1; // string compare
        return intA > intB ? 1 : -1;
      });
    });

    if (!this.galleryPermissions().can_edit) return of(undefined);

    return this.galleryTags$.pipe(
      take(1),
      tap(tags => {
        const removedTags = tags.filter(tag => this.allFilters.tags[tag.id]);
        if (removedTags.length) filtersUpdated = true;
        removedTags.forEach(tag => {
          delete this.allFilters.tags[tag.id];
          for (let i = 0; i < this.filters.length; i++) {
            const filter = this.filters[i];
            if (filter.key !== 'tags') continue;
            filter.values = filter.values.filter(v => v !== tag.id);
            if (!filter.values.length) {
              this.filters.splice(i, 1);
              i--;
            }
          }
        });

        if (filtersUpdated) {
          this.onFilterChange.emit(undefined);
          this.filtersChanged$.next();
        }

        tags.forEach(tag => (this.allFilters.tags[tag.id] = false));
        this.dynamicFiltersCreated.next();
      })
    );
  }

  private handleIncompatibleFilters(toggleName?: string) {
    if (toggleName === 'unseen_activity') {
      if (this.allFilters.unseen_activity.with) {
        this.removeAllFilters();
        this.allFilters.unseen_activity.with = true;
      }
    } else {
      this.allFilters.unseen_activity.with = false;
      if (toggleName === 'people') {
        // remove all feedback negative filters
        this.allFilters.likes.without = false;
        this.allFilters.comments.without = false;
        this.allFilters.annotations.without = false;
        this.allFilters.status.no_status = false;
        this.allFilters.color_label.null = false;
      }
    }
  }

  handleFilter(toggleName?: string) {
    if (toggleName) {
      this.handleIncompatibleFilters(toggleName);
    }
    this.filters.length = 0;
    Object.entries(this.allFilters).forEach(([key, value]) => {
      const filter: Filter = { key, values: [] };
      for (const i in value) {
        const val: boolean | string = i;
        if (value[i]) filter.values.push(val);
      }
      if (filter.values.length) this.filters.push(filter);
    });
    this.onFilterChange.emit(undefined);
    this.filtersChanged$.next();
    if (toggleName) {
      this.analytics.track('toggle-switch', false, { name: toggleName, location: 'am-sidebar' });
    }
  }

  areChanges(key: string) {
    return !!this.filters.find(filter => filter.key === key);
  }

  getGalleryLink(gallery: GalleryDto) {
    const path = location.pathname
      .split(this.gallery.id)
      .join(gallery.id)
      .split(this.gallery.id.split('gal_')[1])
      .join(gallery.id)
      .split(this.gallery.path)
      .join(gallery.path);
    return path.split('/');
  }

  private getVisibleFilters() {
    const filters: typeof this.allFilters = {
      annotations: this.annotationToggler?.open ? this.allFilters.annotations : undefined,
      aperture: this.exif2Toggler?.open ? this.allFilters.aperture : undefined,
      camera: this.exif1Toggler?.open ? this.allFilters.camera : undefined,
      color_label: this.colorLabelToggler?.open ? this.allFilters.color_label : undefined,
      comments: this.commentToggler?.open ? this.allFilters.comments : undefined,
      focalLength: this.exif1Toggler?.open ? this.allFilters.focalLength : undefined,
      iso: this.exif2Toggler?.open ? this.allFilters.iso : undefined,
      lens: this.exif1Toggler?.open ? this.allFilters.lens : undefined,
      likes: this.favouriteToggler?.open ? this.allFilters.likes : undefined,
      people: this.peopleToggler?.open ? this.allFilters.people : undefined,
      shutterSpeed: this.exif2Toggler?.open ? this.allFilters.shutterSpeed : undefined,
      status: this.statusToggler?.open ? this.allFilters.status : undefined,
      tags: this.tagsToggler?.open ? this.allFilters.tags : undefined,
      unseen_activity: this.allFilters.unseen_activity,
      size: this.sizeToggler?.open ? this.allFilters.size : undefined,
    };

    return filters;
  }

  calcAssetCountsDebounced = debounce(() => {
    if (this.people) {
      this.people.forEach(p => {
        if (this.allFilters.people[p.userOrGuest['id']]) return;
        this.allFilters.people[p.userOrGuest['id']] = false; // add new people
      });
    }

    if (this.growthBook.isOn('size-filter')) {
      this._assets.forEach(asset => {
        if (!asset.version_data.size) return;
        const size = calcAssetSizeGroup(BigInt(asset.version_data.size));
        this.allFilters.size[size] = this.allFilters.size[size] || false;
      });
    }

    if (!this.filters) return;
    this.activityService
      .getGalleryAssetUserFeedbackMap(this.gallery, this._assets)
      .pipe(take(1))
      .subscribe(map => {
        const allFilters = this.getVisibleFilters();
        for (const filterKey in allFilters) {
          if (allFilters[filterKey] === undefined) continue;

          const values = allFilters[filterKey];

          let baseFilters = this.filters.filter(filter => filter.key !== filterKey);

          if (filterKey === 'unseen_activity') {
            // remove all other filters
            baseFilters = baseFilters.filter(filter => filter.key === 'unseen_activity');
          } else {
            // remove unseen_activity filter
            baseFilters = baseFilters.filter(filter => filter.key !== 'unseen_activity');
            if (filterKey === 'people') {
              baseFilters = baseFilters.filter(
                filter => !['likes', 'comments', 'annotations', 'status', 'color_label'].includes(filter.key)
              );
            }
          }

          if (exifKeys.includes(filterKey) && Object.values(values).length) {
            const assets = this.filterAssets(map, baseFilters);
            for (const value in values) {
              const filteredAssets = assets.filter(
                asset => !singleFilter({ key: filterKey, values: [value] }, asset, this.state.unseenActivityCountMap)
              );
              this.assetCounts.set(`${filterKey}_${value}`, filteredAssets.length);
            }
          } else {
            for (const value in values) {
              const filters = [...baseFilters, { key: filterKey, values: [value] }];
              const assets = this.filterAssets(map, filters);
              this.assetCounts.set(`${filterKey}_${value}`, assets.length);
            }
          }
        }
        this.peopleToggler?.calcBodyHeight();
      });
  }, 100);

  private filterAssets(map: AssetUserFeedbackMap, filters: Filter[]) {
    return filterAssets(this._assets || [], map, filters, this.searchTerm, false, this.state.unseenActivityCountMap);
  }

  private removeAllFilters() {
    Object.keys(this.allFilters).forEach(key => {
      const g = this.allFilters[key];
      for (const key in g) g[key] = false;
    });
  }

  resetFilters() {
    this.removeAllFilters();
    this.handleFilter();
  }

  selectAll() {
    this.onSelectAll.emit();
  }

  async openSettingsDialog(gallery = this.gallery) {
    this.dialog.open(GallerySettingsMenuComponent, { gallery, defaultMode: 'review' });
  }

  async moveGalleryOut(gallery: GalleryDto) {
    await this.galleryService.updateGallery(gallery.id, { section: null, parent_gallery: null });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
